import * as React from 'react';

export type TelemetryErrorBoundaryProps = {
  children: React.ReactNode;
  /** Show error details (stack/message) in the fallback. Set to true in dev mode. */
  isDev?: boolean;
  /**
   * Heading text for the error fallback UI.
   * Defaults to English to avoid a dependency on the i18n provider:
   * this boundary must remain renderable when the i18n provider itself has crashed.
   */
  label?: string;
  /**
   * Description text for the error fallback UI.
   * Defaults to English for the same reason as `label`.
   */
  message?: string;
  /** Called when a descendant throws during rendering. Use to report to telemetry. */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
};

type TelemetryErrorBoundaryState = {
  error: Error | null;
  hasError: boolean;
};

/**
 * React class error boundary for top-level telemetry error capture.
 *
 * Place this around RouterProvider to catch catastrophic render errors that
 * TanStack Router's own error handling cannot intercept. When an error is
 * caught, `onError` is called (tolerant of throwing callbacks), and a
 * context-free fallback is rendered that does not depend on any React context
 * (router, i18n, etc.) — making it safe to use even when the router has not
 * yet mounted.
 *
 * ## Why i18n is not used here
 *
 * `label` and `message` are plain string props (defaulting to English) rather
 * than i18n keys. This boundary must render correctly even if the i18n provider
 * itself has crashed, and using `useTranslation()` inside a class component is
 * not possible. Callers can pass translated strings as props when the translation
 * context is available above this component in the tree.
 *
 * ## Why DefaultErrorPage is not used as fallback
 *
 * `DefaultErrorPage` calls `useRouter()` which requires being inside a
 * RouterProvider tree. The inline fallback here is visually equivalent and
 * context-independent.
 */
export class TelemetryErrorBoundary extends React.Component<TelemetryErrorBoundaryProps, TelemetryErrorBoundaryState> {
  constructor(props: TelemetryErrorBoundaryProps) {
    super(props);
    this.state = { error: null, hasError: false };
  }

  static getDerivedStateFromError(error: Error): TelemetryErrorBoundaryState {
    return { error, hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    try {
      this.props.onError?.(error, errorInfo);
    } catch {
      // Swallowed — telemetry callback must never cause a secondary app error.
    }
  }

  render(): React.ReactNode {
    if (!this.state.hasError || !this.state.error) {
      return this.props.children;
    }

    const {
      isDev = false,
      label = 'Something went wrong',
      message = 'An unexpected error occurred. Please refresh the page.',
    } = this.props;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="m-0 text-3xl font-bold">{label}</h1>
        <p className="m-0 text-muted-foreground">{message}</p>
        {isDev && (
          <pre className="max-w-2xl overflow-auto rounded bg-destructive/10 p-4 text-start text-sm text-destructive">
            {this.state.error.stack ?? this.state.error.message}
          </pre>
        )}
        <button
          className="cursor-pointer rounded border px-4 py-2"
          onClick={(): void => {
            window.location.reload();
          }}
          type="button"
        >
          Refresh
        </button>
      </div>
    );
  }
}
